import React, { useState, useEffect } from "react";
import { Container, Form, Row, Col, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";
import { submitForm } from "../../Helpers/API/formsAPI";
import { getRegionConfig } from "./config/regionConfig";


function AwardingMayaCredit({ navigation, primaryDetails, setPrimaryDetails }) {
  const navigate = useNavigate();
  const [isError, setIsError] = useState({
    received_maya_credits: false,
    change_incentive: false,
    verified_maya_account_photo: false,
    maya_account_name: false,
    maya_account_number: false,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Show extra fields only when "No" is selected
  const showExtraFields = primaryDetails.received_maya_credits === "no";

  // Show maya fields only when NOT changing to merienda
  const showMayaFields = primaryDetails.change_incentive !== "yes_change_to_merienda";

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        verified_maya_account_photo_temp_path: uploadResult.temp_path
      }));
      if (isError.verified_maya_account_photo) {
        setIsError(prev => ({ ...prev, verified_maya_account_photo: false }));
      }
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
    }));
  }, [setPrimaryDetails]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return;

    setPrimaryDetails(prev => {
      const updated = { ...prev, [name]: value };

      // Clear extra fields when switching to "yes"
      if (name === "received_maya_credits" && value === "yes") {
        updated.change_incentive = "";
        updated.verified_maya_account_photo_temp_path = "";
        updated.maya_account_name = "";
        updated.maya_account_number = "";
      }

      // Clear maya fields when switching to merienda
      if (name === "change_incentive" && value === "yes_change_to_merienda") {
        updated.verified_maya_account_photo_temp_path = "";
        updated.maya_account_name = "";
        updated.maya_account_number = "";
      }

      return updated;
    });

    if (isError[name]) {
      setIsError(prev => ({ ...prev, [name]: false }));
    }
  };

  const validateForm = () => {
    const errors = {
      received_maya_credits: !primaryDetails.received_maya_credits,
      change_incentive: showExtraFields && !primaryDetails.change_incentive,
      verified_maya_account_photo: showExtraFields && showMayaFields && !primaryDetails.verified_maya_account_photo_temp_path,
      maya_account_name: showExtraFields && showMayaFields && !primaryDetails.maya_account_name,
      maya_account_number: showExtraFields && showMayaFields && !primaryDetails.maya_account_number,
    };

    setIsError(errors);

    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      toast.error("Please fill in all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    const isMayaSelected = primaryDetails.received_maya_credits === "no" && primaryDetails.change_incentive !== "yes_change_to_merienda";

    return !!primaryDetails.received_maya_credits &&
      (!showExtraFields || !!primaryDetails.change_incentive) &&
      (!isMayaSelected || (
        !!primaryDetails.verified_maya_account_photo_temp_path &&
        !!primaryDetails.maya_account_name &&
        !!primaryDetails.maya_account_number
      ));
  };
const handleNext = () => {
  if (validateForm()) {
    if (primaryDetails.received_maya_credits === "no") {
      // "No" → show confirm modal, submit directly
      setShowConfirmModal(true);
    } else {
      // "Yes" → go to awarding-details
      if (navigation && navigation.go) {
        navigation.go("awarding-details");
      }
    }
  }
};

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const regionConfig = getRegionConfig(primaryDetails?.region);
      const res = await submitForm(primaryDetails, regionConfig);

      if (res?.data) {
        try {
          localStorage.removeItem('maya-form-data');
          localStorage.removeItem('maya-form-details');
          localStorage.removeItem('maya-form-current-step');
          localStorage.removeItem('maya-form-history');
          localStorage.setItem('form_completed', 'true');
        } catch (e) {}
        setShowConfirmModal(false);
        navigate('/timein', { replace: true });
      } else {
        toast.error("Submit failed. Please try again.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
      }
    } catch (err) {
      toast.error("Something went wrong while submitting.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
  };

  const handleBack = () => {
    if (navigation && navigation.previous) {
      navigation.previous();
    }
  };

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">Awarding Maya Credit</h4>
              <p className="form-desc">Please provide the Maya Credit awarding details</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>

                  {/* Received Maya Credits */}
                  <Form.Group className="mb-4" controlId="received_maya_credits">
                    <Form.Label className="form-label">
                      Received Maya Credits <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="received_maya_credits"
                      value={primaryDetails.received_maya_credits || ""}
                      onChange={handleInputChange}
                      className={`form-input ${isError.received_maya_credits ? "error" : ""}`}
                    >
                      <option value="">Select an option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Form.Select>
                    {isError.received_maya_credits && (
                      <div className="form-error">Please select an option.</div>
                    )}
                  </Form.Group>

                  {/* Show these fields only when "No" is selected */}
                  {showExtraFields && (
                    <>
                      {/* Would you like to change your incentive? */}
                      <Form.Group className="mb-4" controlId="change_incentive">
                        <Form.Label className="form-label">
                          Would You Like to Change Your Incentive? <span className="form-required">*</span>
                        </Form.Label>
                        <Form.Select
                          name="change_incentive"
                          value={primaryDetails.change_incentive || ""}
                          onChange={handleInputChange}
                          className={`form-input ${isError.change_incentive ? "error" : ""}`}
                        >
                          <option value="">Select an option</option>
                          <option value="no_still_maya_credit">No, Still Maya Credit</option>
                          <option value="yes_change_to_merienda">Yes, Change to Merienda</option>
                        </Form.Select>
                        {isError.change_incentive && (
                          <div className="form-error">Please select an option.</div>
                        )}
                      </Form.Group>

                      {/* Show maya fields only when NOT changing to merienda */}
                      {showMayaFields && (
                        <>
                          {/* Upload Verified Maya Account Photo */}
                          <Form.Group className="mb-4">
                            <Form.Label className="form-label">
                              Upload Verified Maya Account Photo <span className="form-required">*</span>
                              {primaryDetails.verified_maya_account_photo_temp_path && !isUploading && (
                                <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                              )}
                            </Form.Label>
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoUpload}
                              isInvalid={isError.verified_maya_account_photo}
                              disabled={isUploading}
                            />
                            {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                            {isError.verified_maya_account_photo && (
                              <div className="form-error">Required</div>
                            )}
                          </Form.Group>

                          {/* MAYA Account Name */}
                          <Form.Group className="mb-4" controlId="maya_account_name">
                            <Form.Label className="form-label">
                              MAYA Account Name <span className="form-required">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="maya_account_name"
                              value={primaryDetails.maya_account_name || ""}
                              onChange={handleInputChange}
                              placeholder="Enter account name"
                              className={`form-input ${isError.maya_account_name ? "error" : ""}`}
                            />
                            {isError.maya_account_name && (
                              <div className="form-error">Please enter MAYA Account Name.</div>
                            )}
                          </Form.Group>

                          {/* MAYA Account No. */}
                          <Form.Group className="mb-4" controlId="maya_account_number">
                            <Form.Label className="form-label">
                              MAYA Account No. <span className="form-required">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="maya_account_number"
                              value={primaryDetails.maya_account_number || ""}
                              onChange={handleInputChange}
                              placeholder="Enter account number"
                              className={`form-input ${isError.maya_account_number ? "error" : ""}`}
                            />
                            {isError.maya_account_number && (
                              <div className="form-error">Please enter MAYA Account No.</div>
                            )}
                          </Form.Group>
                        </>
                      )}
                    </>
                  )}

                  {/* Navigation Buttons */}
                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      {primaryDetails.received_maya_credits === "no" ? "Submit" : "Next →"}
                    </AppButton>
                  </div>

                </Form>
              </div>
            </div>
          </Col>
        </Row>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={handleCancelSubmit} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center fw-bold text-primary">
              ⚠️ Confirm Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="mb-3 text-dark">Are you sure you want to proceed?</h5>
            <p className="text-muted mb-0">
              <strong>Once submitted, you can't go back to make changes.</strong>
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center pt-0">
            <Button
              variant="outline-secondary"
              onClick={handleCancelSubmit}
              className="px-4 me-3"
              style={{ minWidth: '100px' }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSubmit}
              className="px-4"
              style={{ minWidth: '100px', backgroundColor: '#007bff' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Submitting...
                </>
              ) : (
                'Yes, Proceed'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </div>
  );
}

export default AwardingMayaCredit;